// THE EXISTING FLOOR, AND ONLY THE ROOMS THAT DIFFER.
//
// Jacky, 15 October 2026 (Bug / Improve, Proposal 041c4596): "some site such as this one have multiple remove and dispose …
// Kitchen area (excluding bench) are existing laminate, while all other room area except wet area are carpet"; and on the
// design, 16 October: "dont make me go select each room. 99% of time its same existing cross all scope area, I just need to
// register the exception". One choice for the site; a room appears here only when someone adds it. The removal lines follow
// (contracts/proposal-removal.ts): one per floor, each at the catalogue's rate for that floor.
import {useState} from 'react';
import {removalGroups,syncRemovalLines,effectiveExistingFloor,type ProposalContent} from '@artemis-group/aiw-contracts';
import {useT} from '../lib/i18n';
import {inputCls} from './job-bits';
import type {PricingBase} from '../lib/proposal-pricing';

type Floor=NonNullable<PricingBase['removal']>[number];

export function RemovalFloors({doc,floors,onChange}:{doc:ProposalContent;floors:Floor[];onChange:(next:ProposalContent)=>void}){
 const {t}=useT();
 const [pendingRoom,setPendingRoom]=useState('');
 const rates=floors.map(f=>({floor:f.floor,sell:f.sell}));
 const site=doc.assessment.existing_floor;
 const choices=[...floors.map(f=>f.floor),'None'];
 const inScope=doc.assessment.rooms.filter(r=>!r.out_of_scope&&!r.is_void&&!!(r.quoted_finish||r.flooring));
 const exceptions=inScope.filter(r=>r.existing_floor&&r.existing_floor.toLowerCase()!==site.toLowerCase());
 const candidates=inScope.filter(r=>!exceptions.includes(r));
 const hasLine=doc.services.some(l=>l.service_id==='remove_dispose'&&l.source!=='custom');
 const apply=(assessment:ProposalContent['assessment'])=>onChange(syncRemovalLines({...doc,assessment},rates));
 const setRoom=(id:string,floor:string)=>apply({...doc.assessment,rooms:doc.assessment.rooms.map(r=>r.id===id?{...r,existing_floor:floor}:r)});
 const groups=removalGroups(doc);
 const floorSelect=(value:string,onPick:(v:string)=>void,label:string,blank:string)=><select aria-label={label} className={inputCls} value={value} onChange={e=>onPick(e.target.value)}>
  <option value="">{blank}</option>{choices.map(c=><option key={c} value={c}>{c==='None'?t('removal.none'):c}</option>)}
 </select>;
 return <section className="rounded-lg border bg-card p-4 text-sm" aria-label={t('removal.title')}>
  <h3 className="font-display font-semibold">{t('removal.title')}</h3>
  <p className="mt-1 text-muted-foreground">{t('removal.hint')}</p>
  <label className="mt-3 flex flex-wrap items-center gap-2">{t('removal.site')} {floorSelect(site,v=>apply({...doc.assessment,existing_floor:v}),t('removal.site'),t('removal.notRecorded'))}</label>
  {!!site&&<div className="mt-3">
   <p className="font-medium">{t('removal.exceptions')}</p>
   <ul className="mt-1 space-y-1">{exceptions.map(r=><li key={r.id} className="flex flex-wrap items-center gap-2">
    <span className="min-w-[120px]">{r.name}</span>
    {floorSelect(r.existing_floor??'',v=>setRoom(r.id,v),`${r.name} · ${t('removal.title')}`,t('removal.sameAsSite',{floor:site}))}
    <button type="button" className="text-micro underline" onClick={()=>setRoom(r.id,'')}>{t('removal.remove')}</button>
   </li>)}</ul>
   {candidates.length>0&&<div className="mt-2 flex flex-wrap items-center gap-2">
    <select aria-label={t('removal.pickRoom')} className={inputCls} value={pendingRoom} onChange={e=>setPendingRoom(e.target.value)}>
     <option value="">{t('removal.addRoom')}</option>{candidates.map(r=><option key={r.id} value={r.id}>{r.name}</option>)}
    </select>
    {!!pendingRoom&&floorSelect('',v=>{if(!v)return;setRoom(pendingRoom,v);setPendingRoom('');},t('removal.pickFloor'),t('removal.pickFloor'))}
   </div>}
   {groups.length>0&&<p className="mt-3 text-muted-foreground">{t('removal.summary',{groups:groups.map(g=>`${g.floor} ${g.area_m2} m²`).join(' · ')})}</p>}
   {!hasLine&&<p className="mt-2 text-muted-foreground">{t('removal.noLine')}</p>}
   {inScope.some(r=>!effectiveExistingFloor(doc,r))&&<p className="mt-2 text-warning">{t('removal.unrecorded')}</p>}
  </div>}
 </section>;
}
